import { useCallback, useEffect, useRef, useState } from "react";
import { countOnce } from "@/lib/api";
import { Shell, useSiteConfig } from "./Shell";
import { RadioEngine } from "./radioEngine";
import { useDialHint, useTwitch, HINT_FREQ, HINT_SPAN } from "./radioHint";

const clamp = (v) => Math.max(0, Math.min(999, v));
const pad = (n) => String(n).padStart(3, "0");
const PX_PER_UNIT = 6;

function signalFor(freq, cfg, failed) {
  if (!cfg) return { light: "red", strength: 0, pinned: false };
  const target = cfg.broadcast_freq;
  let nearest = Infinity;
  for (const s of cfg.stations) if (!failed.has(s.freq)) nearest = Math.min(nearest, Math.abs(s.freq - freq));
  const strength = nearest <= 2 ? 1 - nearest / 3 : 0;
  // An uploaded broadcast that failed to load plays static only, so it never shows LOCKED.
  if (freq === target && !failed.has(target)) return { light: "green", strength: 1, pinned: true };
  if (Math.abs(freq - target) <= 5) return { light: "amber", strength, pinned: false };
  return { light: "red", strength, pinned: false };
}

function SignalLight({ light, hint }) {
  const color = hint ? "var(--w1-amber)" : { red: "var(--w1-red)", amber: "var(--w1-amber)", green: "var(--w1-green)" }[light];
  return (
    <div className="flex items-center gap-3">
      <span data-testid="signal-light-indicator" data-state={light} data-hint={hint ? "1" : undefined}
        className={`block h-4 w-4 rounded-full ${light === "amber" || hint ? "w1-amber-flicker" : ""}`}
        style={{ background: color, boxShadow: `0 0 14px ${color}, 0 0 3px ${color}` }} />
      <span className="text-[10px] tracking-[0.35em] text-[color:var(--w1-muted)]">{light === "green" ? "LOCKED" : light === "amber" ? "CARRIER" : "NO SIGNAL"}</span>
    </div>
  );
}

function Needle({ strength, pinned, kick = 0 }) {
  const [jit, setJit] = useState(0);
  useEffect(() => {
    if (pinned || strength === 0) { setJit(0); return; }
    const id = setInterval(() => setJit((Math.random() - 0.5) * 16), 90);
    return () => clearInterval(id);
  }, [strength, pinned]);
  const angle = pinned ? 58 : Math.max(-62, Math.min(62, -60 + strength * 95 + jit + kick));
  return (
    <div className="relative h-24 w-48 overflow-hidden" data-testid="signal-meter">
      <div className="absolute inset-x-0 bottom-0 h-48 w-48 rounded-full border border-[color:var(--w1-border)]" />
      {[-60, -30, 0, 30, 60].map((a) => (
        <span key={a} className="absolute bottom-0 left-1/2 h-[92px] w-px origin-bottom bg-[color:var(--w1-border)]" style={{ transform: `translateX(-50%) rotate(${a}deg)` }} />
      ))}
      <span data-testid="tuning-needle" data-angle={Math.round(angle)}
        className="absolute bottom-0 left-1/2 h-[88px] w-[2px] origin-bottom"
        style={{ transform: `translateX(-50%) rotate(${angle}deg)`, transition: pinned ? "transform 250ms ease-out" : kick ? "transform 60ms ease-out" : "transform 90ms linear",
          background: pinned ? "var(--w1-green)" : "var(--w1-amber)", boxShadow: "0 0 8px currentColor" }} />
      <span className="absolute bottom-[-6px] left-1/2 h-3 w-3 -translate-x-1/2 rounded-full bg-[color:var(--w1-text)]" />
    </div>
  );
}

function Band({ freq, onDrag }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const onDown = (e) => { ref.current.setPointerCapture(e.pointerId); drag.current = { x: e.clientX, f: freq }; };
  const onMove = (e) => {
    if (!drag.current) return;
    const delta = Math.round((drag.current.x - e.clientX) / PX_PER_UNIT);
    onDrag(clamp(drag.current.f + delta));
  };
  const onUp = () => { drag.current = null; };
  const onWheel = (e) => onDrag(clamp(freq + (e.deltaY > 0 ? 1 : -1) * (Math.abs(e.deltaY) > 40 ? 3 : 1)));
  const ticks = [];
  for (let f = Math.max(0, freq - 70); f <= Math.min(999, freq + 70); f++) ticks.push(f);
  return (
    <div ref={ref} data-testid="radio-dial-container" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onWheel={onWheel}
      className="relative h-28 w-full select-none overflow-hidden border-y border-[color:var(--w1-border)] bg-[color:var(--w1-bg-2)] cursor-grab active:cursor-grabbing"
      style={{ touchAction: "none", boxShadow: "inset 0 0 25px rgba(0,0,0,.85), 0 0 20px rgba(0,240,255,.08)" }}>
      <div className="absolute inset-y-0 left-1/2">
        {ticks.map((f) => (
          <div key={f} className="absolute bottom-0" style={{ left: (f - freq) * PX_PER_UNIT }}>
            <div className="w-px bg-[color:var(--w1-muted)]" style={{ height: f % 10 === 0 ? 34 : f % 5 === 0 ? 20 : 10, opacity: f % 10 === 0 ? 0.9 : 0.45 }} />
            {f % 30 === 0 && <div className="absolute bottom-10 -translate-x-1/2 text-[10px] text-[color:var(--w1-muted)]">{pad(f)}</div>}
          </div>
        ))}
      </div>
      <div className="absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 bg-[color:var(--w1-red)]" style={{ boxShadow: "0 0 10px var(--w1-red)" }} />
      <div className="absolute top-2 left-3 text-[9px] tracking-[0.3em] text-[color:var(--w1-muted)]">DRAG TO TUNE</div>
    </div>
  );
}

function StepButton({ dir, onStep, testid }) {
  const timer = useRef(null);
  const start = () => { onStep(dir); timer.current = setInterval(() => onStep(dir), 90); };
  const stop = () => clearInterval(timer.current);
  return (
    <button type="button" data-testid={testid} onPointerDown={start} onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}
      className="w1-key h-16 w-20 border border-[color:var(--w1-border)] bg-[color:var(--w1-surface)] text-xl text-[color:var(--w1-blue)]" style={{ touchAction: "none" }}>
      {dir < 0 ? "‹" : "›"}
    </button>
  );
}

export default function RadioPage() {
  const cfg = useSiteConfig();
  const [freq, setFreq] = useState(101);
  const [on, setOn] = useState(false);
  const [failed, setFailed] = useState(() => new Set());
  const engine = useRef(null);
  const prevLight = useRef("red");
  const sig = signalFor(freq, cfg, failed);
  const hinted = useDialHint();
  const kick = useTwitch(hinted, freq);
  const hintLight = hinted && sig.light === "red" && Math.abs(freq - HINT_FREQ) <= HINT_SPAN;

  const tune = useCallback((f) => {
    setFreq((cur) => {
      if (f !== cur && engine.current) engine.current.moving();
      return f;
    });
  }, []);
  const step = useCallback((d) => setFreq((cur) => { engine.current?.moving(); return clamp(cur + d); }), []);
  // Desktop: ←/→ tune ±1, Shift+←/→ ±10 (after the tap for sound)
  useEffect(() => {
    if (!on) return undefined;
    const key = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.target.closest?.("input, textarea, select")) return;
      e.preventDefault();
      step((e.key === "ArrowLeft" ? -1 : 1) * (e.shiftKey ? 10 : 1));
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [on, step]);

  useEffect(() => { engine.current?.setFrequency(freq); }, [freq, on]);
  useEffect(() => {
    if (sig.light === "green" && prevLight.current !== "green") engine.current?.click();
    prevLight.current = sig.light;
  }, [sig.light]);
  useEffect(() => () => engine.current?.close(), []);
  useEffect(() => countOnce("radio_visit"), []);
  useEffect(() => {
    if (!on || (freq !== 341 && freq !== 350)) return undefined;
    const id = setTimeout(() => countOnce(`radio_${freq}`), 5000);
    return () => clearTimeout(id);
  }, [on, freq]);

  const powerOn = () => {
    if (!cfg || engine.current) return;
    engine.current = new RadioEngine(cfg, (f) => setFailed((s) => new Set(s).add(f)));
    engine.current.ctx.resume();
    setOn(true);
  };

  return (
    <Shell testid="radio-page">
      <div className="flex-1 flex flex-col px-5 sm:px-12 pt-8 sm:pt-12 max-w-3xl w-full mx-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-[color:var(--w1-muted)]">RADIO 333 // SHORTWAVE</div>
            <div data-testid="radio-frequency-display" className="w1-display mt-3 text-6xl sm:text-7xl text-[color:var(--w1-blue)]" style={{ textShadow: "0 0 18px rgba(0,240,255,.35)" }}>{pad(freq)}</div>
          </div>
          <SignalLight light={sig.light} hint={hintLight} />
        </div>
        <div className="mt-8 flex justify-center"><Needle strength={sig.strength} pinned={sig.pinned} kick={kick} /></div>
        <div className="mt-8 -mx-5 sm:mx-0"><Band freq={freq} onDrag={tune} /></div>
        <div className="mt-6 flex items-center justify-between gap-4">
          <StepButton dir={-1} onStep={step} testid="radio-step-down" />
          {!on ? (
            <button type="button" onClick={powerOn} data-testid="audio-toggle-button" disabled={!cfg}
              className="w1-key flex-1 h-16 border border-[color:var(--w1-blue-dim)] text-xs tracking-[0.35em] text-[color:var(--w1-blue)] bg-transparent">
              TAP FOR SOUND
            </button>
          ) : (
            <div data-testid="audio-on-indicator" className="flex-1 text-center text-[10px] tracking-[0.35em] text-[color:var(--w1-green)] opacity-70">RECEIVING</div>
          )}
          <StepButton dir={1} onStep={step} testid="radio-step-up" />
        </div>
      </div>
    </Shell>
  );
}
